<script lang="ts">
  import { decodeUpload, extractFromDataTransfer, UploadRejected, filenameStem, type SizeClassification } from '$lib/io/upload';
  import { projectStore } from '$lib/state/project.svelte';
  import { setActiveSource } from '$lib/compute/recompute.svelte';
  import { createEmptyPipeline } from '$lib/model/tree';
  import { orientTargetForSource } from '$lib/compute/orient';

  interface Props {
    onMessage?: (message: { kind: 'warn' | 'error'; text: string }) => void;
  }

  const { onMessage }: Props = $props();

  let dragOver = $state(false);
  let inputEl: HTMLInputElement;

  async function handle(file: File | Blob, fallbackName?: string) {
    try {
      const decoded = await decodeUpload(file, fallbackName);
      adoptUpload(decoded);
      if (decoded.classification.kind === 'warn') {
        onMessage?.({
          kind: 'warn',
          text: `Large image (${decoded.classification.megapixels.toFixed(1)} MP) — operations may be slow.`,
        });
      }
    } catch (err) {
      if (err instanceof UploadRejected) {
        onMessage?.({ kind: 'error', text: err.message });
      } else {
        onMessage?.({
          kind: 'error',
          text: 'Upload failed. Try a different file.',
        });
      }
    }
  }

  function adoptUpload(decoded: Awaited<ReturnType<typeof decodeUpload>>) {
    const stem = filenameStem(decoded.asset.filename);
    const fresh = createEmptyPipeline(stem);
    projectStore.name = stem;
    projectStore.pipeline = fresh;
    projectStore.selected_step_id = fresh.root_id;
    projectStore.input_asset = decoded.asset;
    projectStore.layer_order = [];
    // Auto-orient target canvas to match source aspect (landscape source =>
    // landscape target).
    const oriented = orientTargetForSource(
      decoded.bitmap.width,
      decoded.bitmap.height,
      projectStore.settings,
    );
    projectStore.settings.target_width = oriented.width;
    projectStore.settings.target_height = oriented.height;
    setActiveSource(decoded.bitmap, decoded.blob);
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    dragOver = true;
  }
  function onDragLeave() {
    dragOver = false;
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    dragOver = false;
    if (!e.dataTransfer) return;
    const file = extractFromDataTransfer(e.dataTransfer);
    if (file) void handle(file);
  }
  function onPick() {
    inputEl?.click();
  }
  function onFileChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) void handle(file);
    target.value = '';
  }
</script>

<div
  class="zone"
  class:over={dragOver}
  ondragover={onDragOver}
  ondragleave={onDragLeave}
  ondrop={onDrop}
  onclick={onPick}
  onkeydown={(e) => e.key === 'Enter' && onPick()}
  role="button"
  tabindex="0"
  aria-label="Upload an image"
>
  <div class="icon">⬆</div>
  <p class="primary">Drop an image, click to pick, or paste</p>
  <p class="hint">PNG, JPEG, or WebP — up to 64 MP</p>
  <input
    bind:this={inputEl}
    type="file"
    accept="image/png,image/jpeg,image/webp"
    onchange={onFileChange}
    hidden
  />
</div>

<style>
  .zone {
    border: 2px dashed var(--border);
    border-radius: var(--radius);
    padding: 48px 32px;
    text-align: center;
    cursor: pointer;
    transition:
      border-color 120ms,
      background 120ms;
    color: var(--text-dim);
    background: transparent;
  }
  .zone:hover,
  .zone.over,
  .zone:focus-visible {
    border-color: var(--accent);
    background: rgba(255, 140, 66, 0.04);
    outline: none;
  }
  .icon {
    font-size: 28px;
    margin-bottom: var(--gap-s);
    color: var(--accent);
  }
  .primary {
    color: var(--text);
    margin: 0 0 var(--gap-s);
    font-weight: 500;
  }
  .hint {
    font-size: 12px;
    margin: 0;
  }
</style>
